// src/components/InfoPages.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SupportLanguageNotice from './SupportLanguageNotice';
import { useI18n } from '../i18n/I18nContext';
import { getInfoPageCopy } from '../i18n/infoPageCopy';
import { getTarotCopy } from '../i18n/tarotCopy';
import { getSecurityCopy } from '../i18n/securityCopy';
import { useCookieConsent } from '../context/CookieConsentContext';
import '../style/Homepage.css';
import tarotFool from '../assets/images/tarot-library/00-fool.jpg';
import tarotMagician from '../assets/images/tarot-library/01-magician.jpg';
import tarotHighPriestess from '../assets/images/tarot-library/02-high-priestess.jpg';
import tarotEmpress from '../assets/images/tarot-library/03-empress.jpg';
import tarotEmperor from '../assets/images/tarot-library/04-emperor.jpg';
import tarotHierophant from '../assets/images/tarot-library/05-hierophant.jpg';
import tarotLovers from '../assets/images/tarot-library/06-lovers.jpg';
import tarotSevenCups from '../assets/images/tarot-library/07-seven-of-cups.jpg';
import tarotEightCups from '../assets/images/tarot-library/08-eight-of-cups.jpg';
import tarotHermit from '../assets/images/tarot-library/09-hermit.jpg';
import tarotWheel from '../assets/images/tarot-library/10-wheel-of-fortune.jpg';
import tarotJustice from '../assets/images/tarot-library/11-justice.jpg';
import tarotHangedMan from '../assets/images/tarot-library/12-hanged-man.jpg';
import tarotDeath from '../assets/images/tarot-library/13-death.jpg';
import tarotTemperance from '../assets/images/tarot-library/14-temperance.jpg';
import tarotDevil from '../assets/images/tarot-library/15-devil.jpg';
import tarotTower from '../assets/images/tarot-library/16-tower.jpg';
import tarotStar from '../assets/images/tarot-library/17-star.jpg';
import tarotMoon from '../assets/images/tarot-library/18-moon.jpg';
import tarotSun from '../assets/images/tarot-library/19-sun.jpg';
import tarotJudgement from '../assets/images/tarot-library/20-judgement.jpg';
import tarotWorld from '../assets/images/tarot-library/21-world.jpg';

const tarotLibraryCards = [
  {
    number: '0',
    title: 'The Fool',
    image: tarotFool,
    keywords: ['Beginnings', 'Trust', 'Openness'],
    meaning: 'The Fool marks the first step into something unknown. It invites curiosity, courage, and a willingness to learn without demanding every answer in advance.',
    reflection: 'What would I begin if I trusted myself a little more?',
  },
  {
    number: 'I',
    title: 'The Magician',
    image: tarotMagician,
    keywords: ['Agency', 'Skill', 'Intention'],
    meaning: 'The Magician points to the tools, abilities, and influence already available to you. Progress comes from matching a clear intention with deliberate action.',
    reflection: 'Which ability am I ready to use more deliberately?',
  },
  {
    number: 'II',
    title: 'The High Priestess',
    image: tarotHighPriestess,
    keywords: ['Intuition', 'Mystery', 'Inner knowing'],
    meaning: 'The High Priestess asks for stillness before action. Not everything is visible yet, and listening to subtle feelings may reveal what noise and urgency conceal.',
    reflection: 'What do I already know beneath the noise?',
  },
  {
    number: 'III',
    title: 'The Empress',
    image: tarotEmpress,
    keywords: ['Nurture', 'Abundance', 'Creativity'],
    meaning: 'The Empress represents growth through care, patience, and connection to the body and natural world. What receives steady attention has room to flourish.',
    reflection: 'What needs consistent care in order to grow?',
  },
  {
    number: 'IV',
    title: 'The Emperor',
    image: tarotEmperor,
    keywords: ['Structure', 'Leadership', 'Boundaries'],
    meaning: 'The Emperor brings order, responsibility, and stable foundations. Its strength is useful when it creates safety, but it can become limiting when control replaces flexibility.',
    reflection: 'Where would a clear boundary create more safety?',
  },
  {
    number: 'V',
    title: 'The Hierophant',
    image: tarotHierophant,
    keywords: ['Tradition', 'Teaching', 'Values'],
    meaning: 'The Hierophant speaks of teachers, shared systems, and inherited beliefs. It can signal guidance from experience while asking you to examine which traditions remain meaningful.',
    reflection: 'Which tradition supports me, and which one needs questioning?',
  },
  {
    number: 'VI',
    title: 'The Lovers',
    image: tarotLovers,
    keywords: ['Connection', 'Choice', 'Alignment'],
    meaning: 'The Lovers is about relationship and the choices that reveal personal values. Attraction matters, but lasting alignment asks for honesty, consent, and shared direction.',
    reflection: 'Which choice reflects my values, not only my immediate desire?',
  },
  {
    number: 'Seven of Cups',
    title: 'Seven of Cups',
    image: tarotSevenCups,
    keywords: ['Options', 'Imagination', 'Discernment'],
    meaning: 'The Seven of Cups appears when possibilities multiply. Imagination can inspire, but clarity requires separating a meaningful vision from distraction, fantasy, or avoidance.',
    reflection: 'Which option remains meaningful after the excitement fades?',
  },
  {
    number: 'Eight of Cups',
    title: 'Eight of Cups',
    image: tarotEightCups,
    keywords: ['Departure', 'Honesty', 'Seeking'],
    meaning: 'The Eight of Cups describes leaving something that no longer feels emotionally complete. Walking away can be an act of self-respect rather than failure.',
    reflection: 'What am I ready to outgrow, even if it once mattered?',
  },
  {
    number: 'IX',
    title: 'The Hermit',
    image: tarotHermit,
    keywords: ['Solitude', 'Wisdom', 'Inner guidance'],
    meaning: 'The Hermit creates space for reflection and independent insight. A temporary retreat can help you hear your own judgement before returning to the wider world.',
    reflection: 'What becomes clear when I stop seeking outside approval?',
  },
  {
    number: 'X',
    title: 'Wheel of Fortune',
    image: tarotWheel,
    keywords: ['Cycles', 'Change', 'Timing'],
    meaning: 'The Wheel of Fortune signals a turning point and reminds us that circumstances move in cycles. You may not control every change, but you can choose how to meet it.',
    reflection: 'What change can I work with instead of resisting?',
  },
  {
    number: 'XI',
    title: 'Justice',
    image: tarotJustice,
    keywords: ['Truth', 'Accountability', 'Balance'],
    meaning: 'Justice asks for facts, proportion, and responsibility. It favours decisions that acknowledge consequences and treat everyone involved with fairness.',
    reflection: 'What would an honest and balanced decision require?',
  },
  {
    number: 'XII',
    title: 'The Hanged Man',
    image: tarotHangedMan,
    keywords: ['Pause', 'Surrender', 'Perspective'],
    meaning: 'The Hanged Man suggests that forcing progress may not help. A pause, changed viewpoint, or voluntary release can reveal an option that action alone could not.',
    reflection: 'What changes when I view this situation from another angle?',
  },
  {
    number: 'XIII',
    title: 'Death',
    image: tarotDeath,
    keywords: ['Ending', 'Release', 'Transformation'],
    meaning: 'Death usually represents symbolic closure, not literal death. It marks the point where an old identity, pattern, or chapter must end so change can become real.',
    reflection: 'What ending must I accept in order to move forward?',
  },
  {
    number: 'XIV',
    title: 'Temperance',
    image: tarotTemperance,
    keywords: ['Moderation', 'Integration', 'Healing'],
    meaning: 'Temperance brings different elements into a workable balance. Patient adjustment and thoughtful compromise can create something stronger than either extreme.',
    reflection: 'What can be combined rather than chosen between?',
  },
  {
    number: 'XV',
    title: 'The Devil',
    image: tarotDevil,
    keywords: ['Attachment', 'Temptation', 'Shadow'],
    meaning: 'The Devil highlights habits, desires, or power dynamics that feel difficult to escape. Naming the pattern honestly is often the first step toward recovering choice.',
    reflection: 'Which attachment feels more powerful than it truly is?',
  },
  {
    number: 'XVI',
    title: 'The Tower',
    image: tarotTower,
    keywords: ['Disruption', 'Revelation', 'Rebuilding'],
    meaning: 'The Tower brings a sudden truth or the fall of an unstable structure. Although uncomfortable, the disruption clears space for foundations that are more honest.',
    reflection: 'What truth is asking me to rebuild differently?',
  },
  {
    number: 'XVII',
    title: 'The Star',
    image: tarotStar,
    keywords: ['Hope', 'Renewal', 'Authenticity'],
    meaning: 'The Star offers quiet recovery after difficulty. It represents hope that is grounded in honesty, openness, and a return to what feels genuinely life-giving.',
    reflection: 'What future feels honest enough to believe in?',
  },
  {
    number: 'XVIII',
    title: 'The Moon',
    image: tarotMoon,
    keywords: ['Uncertainty', 'Dreams', 'Subconscious'],
    meaning: 'The Moon describes a landscape shaped by emotion, intuition, and incomplete information. Move carefully, listen inwardly, and verify assumptions before deciding.',
    reflection: 'What fear or projection may be shaping what I see?',
  },
  {
    number: 'XIX',
    title: 'The Sun',
    image: tarotSun,
    keywords: ['Clarity', 'Joy', 'Vitality'],
    meaning: 'The Sun brings visibility, confidence, warmth, and direct understanding. It encourages you to recognise success and share energy without making yourself smaller.',
    reflection: 'What becomes possible when I let myself be fully seen?',
  },
  {
    number: 'XX',
    title: 'Judgement',
    image: tarotJudgement,
    keywords: ['Awakening', 'Review', 'Calling'],
    meaning: 'Judgement invites an honest review of the past without remaining trapped in it. A lesson has matured, and now it asks to become a decision or new direction.',
    reflection: 'What lesson am I ready to act on now?',
  },
  {
    number: 'XXI',
    title: 'The World',
    image: tarotWorld,
    keywords: ['Completion', 'Integration', 'Belonging'],
    meaning: 'The World closes a cycle with perspective and wholeness. It asks you to recognise what has been achieved before crossing the next threshold.',
    reflection: 'What deserves acknowledgement before I begin again?',
  },
];

function InfoLayout({ eyebrow, title, description, children, sections = [] }) {
  const { t } = useI18n();

  return (
    <div>
      <Header />
      <main className="info-page">
        <section className="info-hero">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{description}</p>
          <div className="hero-actions">
            <Link to="/find-teller" className="primary-button">{t('workflow.dashboard.find')}</Link>
            <Link to="/contact" className="secondary-button">{t('workflow.dashboard.contactSupport')}</Link>
          </div>
        </section>

        {sections.length > 0 && (
          <section className="feature-grid info-grid">
            {sections.map((section) => (
              <article className="feature-card" key={section.title}>
                <h3>{section.title}</h3>
                <p>{section.body}</p>
              </article>
            ))}
          </section>
        )}

        {children}
      </main>
      <Footer />
    </div>
  );
}

function StepList({ steps }) {
  return (
    <section className="timeline-section">
      {steps.map(([title, body], index) => (
        <article className="timeline-item" key={title}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <div>
            <h2>{title}</h2>
            <p>{body}</p>
          </div>
        </article>
      ))}
    </section>
  );
}

export const HowItWorksPage = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).how;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))}><StepList steps={copy.steps} /></InfoLayout>;
};


export const SubscribePage = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).pro;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};

export const ContentLibrary = () => {
  const { t, language } = useI18n();
  const copy = getInfoPageCopy(language).library;
  const translatedCards = getTarotCopy(language);

  return (
    <InfoLayout
    eyebrow={copy.eyebrow}
    title={copy.title}
    description={copy.description}
  >
    <section className="tarot-library-intro">
      <div>
        <p className="eyebrow">{copy.guides}</p>
        <h2>{copy.guideTitle}</h2>
      </div>
      <p>{copy.guideBody}</p>
    </section>

    <section className="tarot-deck-grid" aria-label={copy.meanings}>
      {tarotLibraryCards.map((card, index) => {
        const [title, keywords, meaning, reflection] = translatedCards[index] || [card.title, card.keywords, card.meaning, card.reflection];
        return (
        <article className="tarot-library-card" key={card.number}>
          <div className="tarot-card-image-wrap">
            <img src={card.image} alt={`${copy.meanings}: ${title}`} loading="lazy" />
          </div>
          <div className="tarot-card-copy">
            <div className="tarot-card-heading">
              <span>{card.number}</span>
              <h2>{title}</h2>
            </div>
            <div className="tarot-keywords" aria-label={`${copy.themes}: ${title}`}>
              {keywords.map((keyword) => <span key={keyword}>{keyword}</span>)}
            </div>
            <p>{meaning}</p>
            <div className="tarot-reflection">
              <strong>{copy.reflection}</strong>
              <p>{reflection}</p>
            </div>
          </div>
        </article>
      );})}
    </section>

    <section className="tarot-library-note">
      <p className="eyebrow">{copy.note}</p>
      <h2>{copy.noteTitle}</h2>
      <p>{copy.noteBody}</p>
      <Link to="/find-teller" className="primary-button">{t('workflow.dashboard.find')}</Link>
    </section>
    </InfoLayout>
  );
};

export const AllArticles = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).articles;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};

export const HelpPage = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).help;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))}><SupportLanguageNotice /></InfoLayout>;
};

export const legacySecuritySummaryCards = [
  {
    title: 'Fate Room is a venue',
    body: 'We provide the app, profiles, discovery, booking tools, payment instructions, video rooms, and support workflows. Independent tellers provide the readings.',
  },
  {
    title: 'Reading payments go to tellers',
    body: 'When you book a session, you pay the teller directly. Price, timing, and session details are agreed between you and the teller — not with Fate Room as the seller.',
  },
  {
    title: 'Pro is paid to Fate Room',
    body: 'Client Pro and Teller Pro are platform products. Those payments are made to the Fate Room account. If something goes wrong with Pro billing or activation, contact us.',
  },
  {
    title: 'We connect, we do not settle',
    body: 'We can help with contact details, booking records, and safety reports, but we are not responsible for direct teller transactions, refunds, or disputes between users.',
  },
];

export const legacySecuritySections = [
  {
    title: '1. Our role as a marketplace',
    body: [
      'Fate Room operates as a neutral venue for independent fortune tellers and clients. We are not the seller of a reading, not a party to the contract between client and teller, and not a bank or payment escrow service for session fees.',
      'This follows common marketplace practice: the platform facilitates discovery and booking tools, while the actual reading service and its payment are handled between the people involved.',
      'We may improve, limit, or suspend parts of the service for safety, fraud prevention, maintenance, or legal compliance.',
    ],
  },
  {
    title: '2. Booking a teller and agreeing on price',
    body: [
      'Clients choose a teller, reserve an available slot, and arrange session details through the app. The listed price is set by the teller, but the final amount, duration, or special conditions may be discussed directly between client and teller before payment.',
      'Fate Room may display teller pricing, availability, and payment instructions to make booking clearer, but we do not guarantee that every teller will accept every request or that a negotiated price will match the profile default.',
      'Before you pay, confirm the teller name, session date, start time, duration, amount, and any special terms in the app or in your direct conversation with the teller.',
    ],
  },
  {
    title: '3. Direct teller payments — what we do and do not cover',
    body: [
      'For live readings, Fate Room typically shows direct teller payment details such as IBAN, QR code, amount, and variable symbol. The client sends payment to the teller, not to Fate Room.',
      'We do not control bank processing times, teller bank accounts, incorrect transfers, partial payments, duplicate payments, or refunds after money has left the client account. Those matters must be resolved directly between client and teller.',
      'Fate Room may help by showing contact paths, booking status, payment references, and report tools, but we are not responsible for teller non-delivery, missed sessions caused by user-side issues, or financial loss from a direct bank transfer error.',
      'Keep your payment confirmation, variable symbol, booking ID, and screenshots. They are often needed when a teller confirms a booking, when you report abuse, or when either side asks for support documentation.',
    ],
  },
  {
    title: '4. Pro profile payments — when to contact Fate Room',
    body: [
      'Client Pro and Teller Pro are paid directly to the Fate Room platform account or through the payment method shown in the app. These are platform products, not payments to an individual teller.',
      'If Pro payment was sent but access was not activated, the wrong plan was charged, a duplicate charge occurred, or you need help with a Pro receipt, contact Fate Room support promptly with your account email, payment reference, and screenshots.',
      'We can review Pro payment cases because those transactions are made to us. This is different from a reading payment sent directly to a teller, where Fate Room is not the recipient and cannot reverse the transfer.',
    ],
  },
  {
    title: '5. Account and session security',
    body: [
      'Accounts use Firebase Authentication, role-based access, verified identity signals, and secure session handling. You are responsible for keeping your login credentials and email account secure.',
      'Do not share your password, one-time codes, or account access with another person. Report suspected unauthorized access to support as soon as you notice it.',
      'Tellers should keep IBAN and profile information accurate. Clients should verify they are paying the teller shown in the booking before sending money.',
    ],
  },
  {
    title: '6. Google sign-in and account control',
    body: [
      'Google sign-in is handled through Firebase Authentication. Fate Room receives a secure user identifier, display name, email address, and profile photo when available. We do not receive your Google password or request Gmail, Contacts, Drive, or Calendar access.',
      'Signing out ends the Fate Room session on that browser. Revoking Fate Room in your Google Account prevents future Google sign-in, but neither action automatically deletes the Fate Room profile stored in Firebase.',
      'To request access, correction, export, restriction, or deletion, use the Privacy topic on the Contact page. We verify account ownership before disclosing or deleting personal data.',
    ],
  },
  {
    title: '7. Video rooms and conversation privacy',
    body: [
      'Video rooms are session-based and intended only for the booked client and teller. Access may open shortly before the scheduled start time.',
      'Users choose when to allow camera and microphone access. Do not record, stream, publish, or share session content without permission unless required for safety reporting or support review.',
      'We do not guarantee uninterrupted video quality, perfect connectivity, or error-free operation. Report technical problems quickly with device details and screenshots where possible.',
    ],
  },
  {
    title: '8. Reports, abuse, and moderation',
    body: [
      'You can report unsafe behavior, harassment, fake profiles, misleading claims, payment abuse, or policy violations. Include booking ID, payment references, dates, and a clear timeline when possible.',
      'We may warn, restrict, suspend, or remove accounts or content when needed to protect users or the platform. Enforcement decisions may consider booking history, reports, payment evidence, and profile content.',
      'Support can help document a case and connect the right people, but we cannot force a teller to refund a direct payment or guarantee a specific dispute outcome.',
    ],
  },
  {
    title: '9. What to do when something goes wrong',
    body: [
      'For a reading payment issue: contact the teller first with your booking ID and payment proof. If the teller is unresponsive or you believe there was fraud or abuse, report the case to Fate Room support.',
      'For a Pro payment issue: contact Fate Room support directly because Pro is billed to the platform account.',
      'For account, video room, or safety issues: contact support through the Contact page with the email on your account, booking ID, session date, teller name, and concise description of what happened.',
      'Chargebacks, false claims, or repeated abusive reports may lead to account restrictions while a case is reviewed.',
    ],
  },
  {
    title: '10. Data we collect for safety',
    body: [
      'We ask for information that supports account access, booking, payment confirmation, safety, moderation, and a better reading experience. Details are explained in the Privacy Policy.',
      'We use access controls, authentication, database security rules, encrypted transport, and limited internal access to protect information. No online service can guarantee perfect security.',
      'Keep your app updated, protect your email account, and avoid sharing payment screenshots in public channels or with people you do not trust.',
    ],
  },
];

export const SecurityPage = () => {
  const { language } = useI18n();
  const copy = getSecurityCopy(language);

  return (
    <div>
      <Header />
      <main className="info-page terms-page security-page">
        <section className="info-hero terms-hero">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          <p>{copy.description}</p>
          <div className="hero-actions">
            <Link to="/terms" className="secondary-button">{copy.terms}</Link>
            <Link to="/contact" className="primary-button">{copy.contact}</Link>
          </div>
        </section>

        <section className="feature-grid info-grid terms-summary-grid" aria-label={copy.summaryLabel}>
          {copy.cards.map(([title, body]) => (
            <article className="feature-card" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </section>

        <section className="terms-notice">
          <div>
            <p className="eyebrow">{copy.noticeEyebrow}</p>
            <h2>{copy.noticeTitle}</h2>
          </div>
          <p>{copy.noticeBody}</p>
        </section>

        <section className="terms-content" aria-label={copy.detailsLabel}>
          {copy.sections.map(([title, paragraphs]) => (
            <article className="terms-section-card" key={title}>
              <h2>{title}</h2>
              <div>{paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
            </article>
          ))}
        </section>

        <section className="terms-contact-card">
          <div>
            <p className="eyebrow">{copy.helpEyebrow}</p>
            <h2>{copy.helpTitle}</h2>
          </div>
          <p>{copy.helpBody}</p>
        </section>
      </main>
      <Footer />
    </div>
  );
};


export const AccessibilityStatement = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).accessibility;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};

export const CookiePolicy = () => {
  const { language, t } = useI18n();
  const copy = getInfoPageCopy(language).cookie;

  return (
    <div>
      <Header />
      <main className="info-page cookie-drawer-page">
        <section className="cookie-content-area">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h1>{copy.title}</h1>
          <p>{copy.description}</p>

          <div className="cookie-mobile-drawer">
            <CookieDrawerContent copy={copy} />
          </div>

          <section className="cookie-explainer-grid">{copy.rows.map(([title, , body]) => <article key={title}><h2>{title}</h2><p>{body}</p></article>)}</section>
        </section>

        <aside className="cookie-side-drawer" aria-label={t('a11y.cookieSettings')}>
          <CookieDrawerContent copy={copy} />
        </aside>
      </main>
      <Footer />
    </div>
  );
};

function CookieDrawerContent({ copy }) {
  const { preferences, analytics, diagnostics, savePreferences, essentialOnly } = useCookieConsent();
  const [choices, setChoices] = useState({ preferences: false, analytics: false, diagnostics: false });
  const [saved, setSaved] = useState(false);
  const categoryKeys = ['necessary', 'preferences', 'analytics', 'diagnostics'];

  useEffect(() => {
    setChoices({ preferences, analytics, diagnostics });
  }, [preferences, analytics, diagnostics]);

  const save = () => {
    savePreferences(choices);
    setSaved(true);
  };

  return (
    <div className="cookie-drawer-card">
      <div className="cookie-drawer-handle" aria-hidden="true" />
      <p className="eyebrow">{copy.controls}</p>
      <h2>{copy.settings}</h2>
      <p>{copy.review}</p>

      <div className="cookie-toggle-list">
        {copy.rows.map(([title, state, body], index) => {
          const key = categoryKeys[index];
          const required = key === 'necessary';
          return (
          <label className="cookie-toggle-row" key={title}>
            <div>
              <strong>{title}</strong>
              <span>{body}</span>
            </div>
            <span className="cookie-policy-control">
              <em>{state}</em>
              <input
                type="checkbox"
                checked={required || Boolean(choices[key])}
                disabled={required}
                onChange={() => setChoices((current) => ({ ...current, [key]: !current[key] }))}
                aria-label={title}
              />
            </span>
          </label>
          );
        })}
      </div>

      <div className="cookie-drawer-actions">
        <button type="button" className="primary-button" onClick={save}>{copy.save || copy.accept}</button>
        <button type="button" className="secondary-button" onClick={() => { essentialOnly(); setSaved(true); }}>{copy.essentialOnly}</button>
      </div>
      {saved && <p className="cookie-save-status" role="status">Your privacy choices have been saved.</p>}
    </div>
  );
}

export const Leadership = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).leadership;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};



export const SleepAppPage = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).sleep;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};


export const AdminPortalLogin = () => {
  const { language } = useI18n();
  const copy = getInfoPageCopy(language).admin;
  return <InfoLayout eyebrow={copy.eyebrow} title={copy.title} description={copy.description} sections={copy.sections.map(([title, body]) => ({ title, body }))} />;
};
